import { select } from 'd3';
import { memo, useLayoutEffect, useMemo, useRef } from 'react';

import { BASE_COLOURS } from '~config';
import type { BaseColour, ColourByParameter, OptimisationDrillHoleProperties } from '~types';
import { createContinuousScale, interpolateOklch, transformPointsToSvgCoords } from '~utils';
import { Circle, useSvgContext } from '../components';
import { useD3SvgSelection } from '../hooks';

type OptimisationDrillHolesProps = {
  /** Drill holes to render */
  drillHoles: OptimisationDrillHoleProperties[];
  /** Parameter used for colour interpolation */
  colourByParameter: ColourByParameter;
  /** Base colour for the colour scale */
  colour?: BaseColour;
  /** Currently selected hole name (controlled) */
  selectedId: string | null;
  /** Called when a hole is activated/selected */
  onHoleSelect: (hole: OptimisationDrillHoleProperties) => void;
  /** Optional formatter for the title attribute of each hole */
  titleFormatter?: (hole: OptimisationDrillHoleProperties) => string;
};

/** Data attribute used by DrillHole and the selection hook */
const DATA_ATTR = 'data-hole-name' as const;

/**
 * Apply selected/deselected visual state to a drill hole group via D3
 * Moving it outside the component avoid its creation on every render,
 * which would cause performance issues and interfere with D3-managed attributes like fill and stroke.
 */
const applySelected = (group: SVGGElement, isSelected: boolean) => {
  const g = select(group);
  if (!g) return;

  g.classed('selected', isSelected).attr('aria-pressed', isSelected ? 'true' : 'false');
};

/** Build a lookup map from hole_name → drill hole properties. */
const buildHoleLookup = (
  drillHoles: OptimisationDrillHoleProperties[],
): Map<string, OptimisationDrillHoleProperties> => {
  const map = new Map<string, OptimisationDrillHoleProperties>();
  for (const hole of drillHoles) {
    if (hole.hole_name) map.set(hole.hole_name, hole);
  }
  return map;
};

/**
 * Layer responsible for rendering drill holes as SVG circles.
 *
 * React owns the DOM structure (one `<Circle>` per hole); D3 owns
 * the imperative mutations for fill colour, selection, and hover styling.
 * This split avoids re-rendering hundreds of circles when only colour
 * or selection changes.
 *
 * Reads the coordinate transformer from `SvgContext` via `svgData`.
 */
const OptimisationDrillHoles = memo(
  ({
    drillHoles,
    colourByParameter,
    colour = BASE_COLOURS.ORANGE,
    selectedId,
    onHoleSelect,
    titleFormatter,
  }: OptimisationDrillHolesProps) => {
    const layerRef = useRef<SVGGElement | null>(null);
    const { transformer } = useSvgContext();

    const holeByName = useMemo(() => buildHoleLookup(drillHoles), [drillHoles]);

    const { transformed, tooltips } = useMemo(() => {
      if (!transformer) return { transformed: [], tooltips: [] };

      return {
        transformed: transformPointsToSvgCoords(drillHoles, transformer),
        tooltips: titleFormatter ? drillHoles.map(titleFormatter) : [],
      };
    }, [drillHoles, transformer, titleFormatter]);

    // Stable lookup function — rebuilds only when the map changes
    const getItemById = useMemo(() => {
      return (id: string) => holeByName.get(id) ?? null;
    }, [holeByName]);

    useD3SvgSelection<OptimisationDrillHoleProperties>({
      layerRef,
      dataAttribute: DATA_ATTR,
      selectedId,
      getItemById,
      onSelect: onHoleSelect,
      applySelected,
    });

    // Imperatively apply fill colours via D3 (avoids re-rendering every DrillHole on colour change)
    useLayoutEffect(() => {
      const layer = layerRef.current;
      if (!layer || drillHoles.length === 0) return;

      const colourScale = createContinuousScale(drillHoles, colourByParameter, { baseColour: colour });

      select(layer)
        .selectAll<SVGGElement, unknown>(`[${DATA_ATTR}]`)
        .each(function () {
          const holeName = this.getAttribute(DATA_ATTR);
          if (!holeName) return;

          const hole = holeByName.get(holeName);
          if (!hole) return;

          const rawValue = Number(hole[colourByParameter]);
          const fill = Number.isFinite(rawValue)
            ? interpolateOklch(rawValue, colourScale.min, colourScale.max, colour)
            : 'var(--color-primary)';

          select(this).select<SVGCircleElement>('.hole-circle').attr('fill', fill);
        });
    }, [drillHoles, colourByParameter, colour, holeByName]);

    // Memoize markup
    // This is needed to prevent React from re-rendering all circles on every prop change (like colour),
    // which would cause performance issues and break D3-managed attributes like fill and stroke.
    const holesMarkup = useMemo(
      () =>
        transformed.map((hole, index) => (
          <Circle
            key={hole.hole_name ?? `hole-${index}`}
            name={hole.hole_name ?? `hole-${index}`}
            className="hover:scale-[1.5] group-focus-visible:scale-[1.5] group-focus-visible:stroke-(--color-primary) group-focus-visible:stroke-4 group-[&.selected]:scale-[1.5] group-[&.selected]:stroke-4! group-[&.selected]:stroke-primary"
            fill="var(--color-primary)"
            radius={1.25}
            strokeWidth={2}
            transform={`translate(${hole.x}, ${hole.y})`}
            title={tooltips[index]}
          />
        )),
      [transformed, tooltips],
    );

    return (
      <g ref={layerRef} className="drill-holes">
        {holesMarkup}
      </g>
    );
  },
);
OptimisationDrillHoles.displayName = 'OptimisationDrillHoles';

export { OptimisationDrillHoles, type OptimisationDrillHolesProps };
